/**
 * classService.js
 * Class-related Firestore operations
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc,
  addDoc, deleteDoc, query, where, orderBy, onSnapshot,
  serverTimestamp, writeBatch, arrayUnion, arrayRemove
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { COLLECTIONS } from './collections';

/**
 * Get all classes
 */
export async function getAllClasses() {
  const snap = await getDocs(collection(db, COLLECTIONS.CLASSES));
  return snap.docs.map(d => ({ key: d.id, ...d.data() }));
}

/**
 * Get class by ID
 */
export async function getClassById(classId) {
  const snap = await getDoc(doc(db, COLLECTIONS.CLASSES, classId));
  return snap.exists() ? { key: snap.id, ...snap.data() } : null;
}

/**
 * Get classes by teacher
 */
export async function getClassesByTeacher(teacherId) {
  const q = query(
    collection(db, COLLECTIONS.CLASSES),
    where('teacherId', '==', teacherId)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ key: d.id, ...d.data() }));
}

/**
 * Create new class
 */
export async function createClass(data) {
  const docRef = await addDoc(collection(db, COLLECTIONS.CLASSES), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}

/**
 * Update class
 */
export async function updateClass(classId, data) {
  await updateDoc(doc(db, COLLECTIONS.CLASSES, classId), {
    ...data,
    updatedAt: serverTimestamp()
  });
}

/**
 * Add students to class
 */
export async function addStudentsToClass(classId, studentIds) {
  await updateDoc(doc(db, COLLECTIONS.CLASSES, classId), {
    studentIds: arrayUnion(...studentIds),
    updatedAt: serverTimestamp()
  });
}

/**
 * Remove student from class
 */
export async function removeStudentFromClass(classId, studentId) {
  await updateDoc(doc(db, COLLECTIONS.CLASSES, classId), {
    studentIds: arrayRemove(studentId),
    updatedAt: serverTimestamp()
  });
}

/**
 * Delete class
 */
export async function deleteClass(classId) {
  await deleteDoc(doc(db, COLLECTIONS.CLASSES, classId));
}

/**
 * Subscribe to all classes (real-time)
 */
export function subscribeToClasses(callback) {
  return onSnapshot(collection(db, COLLECTIONS.CLASSES), (snap) => {
    const classes = snap.docs.map(d => ({ key: d.id, ...d.data() }));
    callback(classes);
  });
}

/**
 * Subscribe to single class (real-time)
 */
export function subscribeToClass(classId, callback) {
  return onSnapshot(doc(db, COLLECTIONS.CLASSES, classId), (snap) => {
    if (snap.exists()) {
      callback({ key: snap.id, ...snap.data() });
    } else {
      callback(null);
    }
  });
}

export default {
  getAllClasses,
  getClassById,
  getClassesByTeacher,
  createClass,
  updateClass,
  addStudentsToClass,
  removeStudentFromClass,
  deleteClass,
  subscribeToClasses,
  subscribeToClass,
};
